import React from 'react';
import { InteractionStatus } from '@azure/msal-browser';
import { IMsalContext, MsalContext } from '@azure/msal-react';
import { SmokeApi } from "../controller/SmokeApi";
import { WebApiInterop } from "../controller/WebApiInterop";
import { loginRequest } from '../authConfig';
import { ServiceReadings, ServiceReadingsSet } from "../model/Reading";
import { SmokeSummary } from "../model/SmokeSummary";
import { SmokeSummaryDetails } from '../components/SmokeSummaryDetails';
import { IAppContext } from "../model/IAppContext";
import { Accordion, AccordionHeader, AccordionItem, AccordionPanel, Button, makeStyles } from '@fluentui/react-components';
import { withStylesAndContext, withStyles, withStylesAndMsalContext } from "../components/withStyles";
import { SiteHeaderWithoutStyles } from "../components/SiteHeader";
import { TheAppContext } from '../model/AppContext';
import { TrendGraph } from '../components/TrendGraph';
import { Smoke, ServiceSmokes } from '../model/Smoke';
import { SmokeChooser } from '../components/SmokeChooser';
import { SmokeItem } from '../components/SmokeItem';
import { Meat } from "../model/Meat";
import { SmokeFull, ServiceSmokeFull } from "../model/SmokeFull";
import { MeatItem } from '../components/MeatItem';
import { VentLog } from "../model/VentLog";
import { VentLogItem } from '../components/VentLogItem';
import { Therm } from '../model/Therm';
import { ThermItem } from '../components/ThermItem';
import { EditAddSmoke } from '../dialogs/EditAddSmoke';
import { ModalPrompt } from '../dialogs/ModalPrompt';
import { EditAddMeat } from '../dialogs/EditAddMeat';
import { ThermsList } from '../components/ThermsList';
import { slStyles } from "../slStyles";
import { VentLogsList } from '../components/VentLogsList';
import { RexDateTime } from '../Dates/RexDateTime';

export interface SmokeItPageProps
{
    styles: Record<string, string>;
    smokes: Smoke[];
}

export interface SmokeItPageState
{
    smokes: Smoke[];
    selectedSmoke?: Smoke;
    selectedMeats?: Meat[];
    selectedVentLogs?: VentLog[];
    selectedTherms?: Therm[];

    // dialog states

    // smoke
    smokeEditing?: Smoke;
    showEditAddSmokeDialog: boolean;
    isAddingSmoke: boolean;
    smokeDeleting?: Smoke;
    isConfirmingDeleteSmoke?: boolean;

    //meat
    meatEditing?: Meat;
    showEditAddMeatDialog: boolean;
    isAddingMeat: boolean;
    meatDeleting?: Meat;
    isConfirmingDeleteMeat?: boolean;

    oobReadingCount: number;
}

const useStyles = makeStyles(
    {
        container:
        {
            display: 'flex',
            flexDirection: 'column',
        },
        smokeItHeader:
        {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            padding: '10pt',
            background: 'lightgray',
            verticalAlign: 'bottom'
        },
        headerItem:
        {
            display: 'flex',
            alignContent: 'center',
            justifyContent: 'center'
        },
        smokeItem:
        {
            padding: '10pt'
        },
        meats:
        {
            padding: '10pt'
        },
        vents:
        {
            ...slStyles.detailContainer,
            padding: '10pt'
        },
        therms:
        {
            ...slStyles.detailContainer
        }
    });

export class SmokeItPageWithoutStyles extends React.Component<SmokeItPageProps, SmokeItPageState>
{
    context!: IAppContext;
    static contextType = TheAppContext;

    constructor(props: SmokeItPageProps)
    {
        super(props);
        this.state = {
            smokes: [],
            showEditAddSmokeDialog: false,
            isAddingSmoke: false,
            smokeDeleting: undefined,
            showEditAddMeatDialog: false,
            isAddingMeat: false,
            oobReadingCount: 0
        };
    }

    async populateSmokes()
    {
        const smokes: ServiceSmokes = await this.context.WebApi.getSmokesForAuthUser(15);

        this.setState(
            {
                smokes: smokes.TheValue ?? []
            });
    }

    async componentDidMount()
    {
        await this.populateSmokes();
//        await this.onSmokeSelected('D9BBQ2021');
    }

    async onSmokeSelected(smokeId: string)
    {
        const smoke = this.state.smokes.find((smoke) => smoke.SmokeID === smokeId);

        const [smokeFull, ventLogs, therms, oobCount] =
            await Promise.all(
                [
                    this.context.WebApi.getSmokeFull(smokeId),
                    this.context.WebApi.getVentLogs(smokeId, 30),
                    this.context.WebApi.getTherms(smokeId),
                    this.context.WebApi.getOutOfBoundsReadingsCountForSmoke(smokeId)
                ]);

        this.setState(
            {
                selectedSmoke: smoke,
                selectedMeats: smokeFull.TheValue?.Meats,
                selectedVentLogs: ventLogs.TheValue ?? [],
                selectedTherms: therms.TheValue ?? [],
                oobReadingCount: oobCount.TheValue ?? 0,
            });
    }

    stateNoDialogs()
    {
        return {
            showEditAddSmokeDialog: false,
            isAddingSmoke: false,
            isConfirmingDeleteSmoke: false,
            smokeDeleting: undefined,
            showEditAddMeatDialog: false,
            isAddingMeat: false,
            meatDeleting: undefined,
            isConfirmingDeleteMeat: false
        };
    }

    onDismissDialogs()
    {
        this.setState({ ...this.stateNoDialogs() });
    }

    updateOrAddSmokeInSmokes(smokes: Smoke[], smoke: Smoke): Smoke[]
    {
        const idx = smokes.findIndex((s) => s.SmokeID === smoke.SmokeID);
        if (idx >= 0)
            return [...smokes.slice(0, idx), smoke, ...smokes.slice(idx + 1)];

        return [smoke, ...smokes];
    }

    async onSaveSmoke(smoke: Smoke)
    {
        const isAdding = this.state.isAddingSmoke;

        if (isAdding)
        {
            const smokeFull: SmokeFull =
            {
                SmokeID: smoke.SmokeID,
                UserID: smoke.UserID,
                Tenant: smoke.Tenant,
                Description: smoke.Description,
                Date: smoke.Date,
                Weather: smoke.Weather,
                Meats: [],
                VentLog: [],
                BuriedWood: smoke.BuriedWood,
                FloatWood: smoke.FloatWood,
                Charcoal: smoke.Charcoal,
                SmokerPrep: smoke.SmokerPrep
            };

            await this.context.WebApi.addSmoke(smokeFull);
        }
        else
            await this.context.WebApi.updateSmoke(smoke);

        this.setState(
            prevState => (
                {
                    ...this.stateNoDialogs(),
                    smokes: this.updateOrAddSmokeInSmokes(prevState.smokes, smoke),
                    selectedSmoke: smoke
                }));
    }

    addSmoke()
    {
        this.setState(
            {
                showEditAddSmokeDialog: true,
                isAddingSmoke: true,
                smokeEditing: new Smoke(),

            });
    }

    editSmoke(event: React.MouseEvent<HTMLButtonElement>, smoke: Smoke)
    {
        this.setState(
            {
                showEditAddSmokeDialog: true,
                isAddingSmoke: false,
                smokeEditing: smoke
            });
        event.stopPropagation();
    }

    deleteSmoke(event: React.MouseEvent<HTMLButtonElement>, smoke: Smoke)
    {
        this.setState(
            prevState => (
                {
                    isConfirmingDeleteSmoke: true,
                    smokeDeleting: prevState.selectedSmoke
                }));

        event.stopPropagation();
    }

    async onDeleteSmokeConfirmed()
    {
        const smoke = this.state.selectedSmoke;

        if (smoke)
        {
            await this.context.WebApi.deleteSmoke(smoke.SmokeID);
            this.setState(
                {
                    ...this.stateNoDialogs(),
                    smokes: this.state.smokes.filter((s) => s.SmokeID !== smoke.SmokeID),
                    selectedSmoke: undefined
                });
        }
    }

    async deleteOOBReadings()
    {
        if (!this.state.selectedSmoke)
            throw new Error("No smoke selected");

        if (!this.state.selectedSmoke)
            throw new Error("No smoke selected");

        await this.context.WebApi.deleteOutOfBoundsReadingsForSmoke(this.state.selectedSmoke?.SmokeID ?? "");
    }

    updateOrAddMeat(meats: Meat[], meat: Meat): Meat[]
    {
        const idx = meats.findIndex((m) => m.MeatID === meat.MeatID);

        if (idx >= 0)
            return [...meats.slice(0, idx), meat, ...meats.slice(idx + 1)];

        return [meat, ...meats];
    }

    async onDeleteMeatConfirmed()
    {
        const meat = this.state.meatDeleting;
        if (meat)
        {
            await this.context.WebApi.deleteMeat(meat.MeatID);
            this.setState(
                {
                    ...this.stateNoDialogs(),
                    selectedMeats: this.state.selectedMeats?.filter((m) => m.MeatID !== meat.MeatID)
                });
        }
    }

    async onSaveMeat(meat: Meat)
    {
        const isAdding = this.state.isAddingMeat;
        if (!meat.SmokeID)
            throw new Error("SmokeID is required");

        if (isAdding)
            await this.context.WebApi.addMeat(meat);
        else
            await this.context.WebApi.updateMeat(meat);

        this.setState(
            {
                ...this.stateNoDialogs(),
                selectedMeats: this.updateOrAddMeat(this.state.selectedMeats ?? [], meat)
            });
    }

    editMeat(event: React.MouseEvent<HTMLButtonElement>, meat: Meat)
    {
        this.setState(
            {
                showEditAddMeatDialog: true,
                isAddingMeat: false,
                meatEditing: meat
            });
        event.stopPropagation();
    }

    addMeat()
    {
        if (!this.state.selectedSmoke)
            throw new Error("No smoke selected");

        const newMeat = new Meat();

        newMeat.SmokeID = this.state.selectedSmoke!.SmokeID;

        this.setState(
            {
                showEditAddMeatDialog: true,
                isAddingMeat: true,
                meatEditing: newMeat
            });
    }

    deleteMeat(event: React.MouseEvent<HTMLButtonElement>, meat: Meat)
    {
        this.setState(
            prevState => (
                {
                    isConfirmingDeleteMeat: true,
                    meatDeleting: meat
                }));
        event.stopPropagation();
    }

    getDeleteInfo(): { title: string, body: string, onSave: () => void, open: boolean } | undefined
    {
        if (this.state.isConfirmingDeleteMeat)
        {
            return {
                title: "Delete Meat?",
                body: `Are you sure you want to delete ${this.state.meatDeleting?.Description}?`,
                onSave: this.onDeleteMeatConfirmed.bind(this),
                open: this.state.isConfirmingDeleteMeat
            };
        }
        else if (this.state.isConfirmingDeleteSmoke)
        {
            return {
                title: "Delete Smoke?",
                body: `Are you sure you want to delete ${this.state.smokeDeleting?.Description}?`,
                onSave: this.onDeleteSmokeConfirmed.bind(this),
                open: this.state.isConfirmingDeleteSmoke
            };
        }
    }

    async deleteTherm(therm: Therm)
    {
        await this.context.WebApi.deleteTherm(therm);
        this.setState(
            {
                selectedTherms: this.state.selectedTherms?.filter((t) => t.ThermID !== therm.ThermID)
            });
    }

    updateOrAddTherm(therms: Therm[], therm: Therm): Therm[]
    {
        const idx = therms.findIndex((t) => t.ThermID === therm.ThermID);

        if (idx >= 0)
            return [...therms.slice(0, idx), therm, ...therms.slice(idx + 1)];

        return [therm, ...therms];
    }

    async addOrSaveTherm(therm: Therm, isAdding: boolean)
    {
        if (isAdding)
        {
            await this.context.WebApi.addTherm(therm);
        }
        else
        {
            await this.context.WebApi.updateTherm(therm);
        }

        this.setState(
            {
                selectedTherms: this.updateOrAddTherm(this.state.selectedTherms ?? [], therm)
            });
    }

    updateOrAddVentLog(ventLogs: VentLog[], ventLog: VentLog): VentLog[]
    {
        const idx = ventLogs.findIndex((v) => (v.Timestamp as RexDateTime).utcValue  === (ventLog.Timestamp as RexDateTime).utcValue);
        if (idx >= 0)
            return [...ventLogs.slice(0, idx), ventLog, ...ventLogs.slice(idx + 1)];
        return [ventLog, ...ventLogs];
    }

    async addOrSaveVentLog(ventLog: VentLog, isAdding: boolean)
    {
        if (isAdding)
        {
            await this.context.WebApi.addVentLog(ventLog);
        }
        else
        {
            await this.context.WebApi.updateVentLog(ventLog);
        }
        this.setState(
            {
                selectedVentLogs: this.updateOrAddVentLog(this.state.selectedVentLogs ?? [], ventLog)
            });
    }

    deleteVentLog(ventLog: VentLog)
    {
        this.setState(
            {
                selectedVentLogs: this.state.selectedVentLogs?.filter((v) => v.Timestamp !== ventLog.Timestamp)
            });
    }

    render()
    {
        // Note, delete meat is not implemented in the service. So we leave off onDelete={ this.deleteMeat.bind(this) }
        const meats = this.state.selectedMeats?.map((meat) => <MeatItem key={meat.MeatID} meat={meat} onEdit={this.editMeat.bind(this)}/>);
        const vents = this.state.selectedVentLogs?.map((vent, idx) => <VentLogItem key={idx} ventLog={vent}/>);
        const therms = this.state.selectedTherms?.map((therm) => <ThermItem key={therm.ThermID} therm={therm}/>);
        const ventLogsList = (<VentLogsList
                                  smoke={this.state.selectedSmoke}
                                  ventLogs={this.state.selectedVentLogs}
                                  onDelete={this.deleteVentLog.bind(this)}
                                  onCommit={this.addOrSaveVentLog.bind(this)}/>);

        const thermsList = (<ThermsList
                                smoke={this.state.selectedSmoke}
                                therms={this.state.selectedTherms}
                                onDelete={this.deleteTherm.bind(this)}
                                onCommit={this.addOrSaveTherm.bind(this)}/>
        );

        const onDismiss = this.onDismissDialogs.bind(this);

        const smoke = this.state.smokeEditing;
        const meat = this.state.meatEditing;

        const oobReadings = this.state.oobReadingCount > 0 &&
            (<div>
                <Button onClick={() => this.deleteOOBReadings()}>Delete {this.state.oobReadingCount} OOB Readings</Button>
            </div>);


        const selectedSmokeId = this.state.selectedSmoke ? this.state.selectedSmoke.SmokeID : "";

        const editAddSmokeDialog = (
            <EditAddSmoke
                open={this.state.showEditAddSmokeDialog}
                isAdd={this.state.isAddingSmoke}
                visible={false}
                onDismiss={onDismiss}
                itemEditing={this.state.smokeEditing}
                onSave={this.onSaveSmoke.bind(this)}/>);

        const editAddMeatDialog = (
            <EditAddMeat
                open={this.state.showEditAddMeatDialog}
                isAdd={this.state.isAddingMeat}
                visible={false}
                onDismiss={onDismiss}
                itemEditing={this.state.meatEditing}
                onSave={this.onSaveMeat.bind(this)}/>);

        const deleteInfo = this.getDeleteInfo();
        const confirmDeleteDialog = (
            <ModalPrompt title={deleteInfo?.title ?? ""} confirm="Delete" onDismiss={onDismiss} onSave={deleteInfo?.onSave} open={deleteInfo?.onSave} visible={false}>
                Are you sure you want to delete {deleteInfo?.body ?? ""}?
            </ModalPrompt>);

        return (
            <div className={this.props.styles.container}>
                <div className={this.props.styles.smokeItHeader}>
                    <div className={this.props.styles.headerItem}>current Smoke:</div>
                    <div className={this.props.styles.headerItem}>
                        <SmokeChooser smokes={this.state.smokes} selectedSmokeId={ selectedSmokeId} onSelected={this.onSmokeSelected.bind(this)}/>
                    </div>
                    <div>
                        <Button onClick={() => this.addSmoke()}>Add Smoke</Button>
                    </div>
                </div>
                {oobReadings}
                <Accordion collapsible multiple defaultOpenItems="0">
                    <AccordionItem value="0">
                        <AccordionHeader size="extra-large">Smoke Detail</AccordionHeader>
                        <AccordionPanel>
                            <div className={this.props.styles.smokeItem}>
                                {this.state.selectedSmoke && <SmokeItem smoke={this.state.selectedSmoke} onEdit={this.editSmoke.bind(this)} onDelete={this.deleteSmoke.bind(this)} />}
                            </div>
                        </AccordionPanel>
                    </AccordionItem>
                    <AccordionItem value="1">
                        <AccordionHeader size="extra-large">Meats</AccordionHeader>
                        <AccordionPanel>
                            <div className={this.props.styles.meats}>
                                {meats}
                                <div>
                                    <Button onClick={() => this.addMeat()}>Add Meat</Button>
                                </div>
                            </div>
                        </AccordionPanel>
                    </AccordionItem>
                    <AccordionItem value="2">
                        <AccordionHeader size="extra-large">Vents</AccordionHeader>
                        <AccordionPanel>
                            <div className={this.props.styles.vents}>
                                {this.state.selectedVentLogs && ventLogsList}

                                <table>
                                    <thead>
                                    <tr>
                                        <th>Time</th>
                                        <th>Vent1</th>
                                        <th>Vent2</th>
                                        <th>Vent3</th>
                                        <th>Comment</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {vents}
                                    </tbody>
                                </table>
                            </div>
                        </AccordionPanel>
                    </AccordionItem>
                    <AccordionItem value="3">
                        <AccordionHeader size="extra-large">Thermometer Definitions</AccordionHeader>
                        <AccordionPanel>
                            <div className={this.props.styles.therms}>
                                {this.state.selectedTherms && thermsList}
                                <table>
                                    <thead>
                                    <tr>
                                        <th>ThermID</th>
                                        <th>Description</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {therms}
                                    </tbody>
                                </table>
                            </div>
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
                {smoke && editAddSmokeDialog}
                {meat && editAddMeatDialog}
                {deleteInfo && confirmDeleteDialog}
            </div>);
    }
}

export const SmokeItPage = withStylesAndMsalContext(useStyles, SmokeItPageWithoutStyles);